import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const WaterManagementPage = () => {
  const [activeTab, setActiveTab] = useState('paddy');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // SEO設定
    document.title = 'Farmo水管理システム販売｜スマホで遠隔水管理・月額料金不要｜佐賀・福岡・鹿児島｜IDS教習所';
    
    // メタタグの設定
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Farmo水管理システム正規販売店。スマホで水田の水位確認・給水操作が可能。月額料金不要で製品代のみ。累計出荷台数3万台突破の実績。省力化・品質向上・適切な水管理でお米の収量アップを実現。全国対応の通信エリア。佐賀・福岡・鹿児島IDS教習所。無料相談受付中。');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Farmo,水管理システム,水田管理,遠隔水管理,スマート農業,IoT農業,水位管理,給水制御,スマホ管理,月額料金不要,省力化,品質向上,収量アップ,水稲,お米,佐賀県,福岡県,鹿児島県,IDS教習所,出荷台数3万台,累計3万台');
    }

    // Canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://ids-group.jp/water-management');
    if (!document.querySelector('link[rel="canonical"]')) {
      document.head.appendChild(canonicalLink);
    }

    // JSON-LD構造化データの追加
    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Farmo水管理システム",
      "description": "累計3万台突破。スマホで簡単遠隔水管理。省力化・品質向上・月額料金不要。水田の給排水を自動化し作業時間を大幅削減。全国通信対応。",
      "brand": {
        "@type": "Brand",
        "name": "Farmo",
        "url": "https://www.farmo.co.jp/"
      },
      "category": "水管理システム",
      "image": "https://ids-group.jp/images/farmo-water-management.jpg",
      "url": "https://ids-group.jp/water-management",
      "offers": {
        "@type": "Offer",
        "price": 0,
        "priceCurrency": "JPY",
        "priceValidUntil": "2026-12-31",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "IDS教習所グループ",
          "url": "https://ids-group.jp",
          "telephone": "+81952719855"
        }
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.7",
        "reviewCount": "30000",
        "bestRating": "5",
        "worstRating": "1"
      },
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "累計出荷台数",
          "value": "30000台以上"
        },
        {
          "@type": "PropertyValue",
          "name": "月額料金",
          "value": "不要"
        },
        {
          "@type": "PropertyValue",
          "name": "通信エリア",
          "value": "全国対応"
        },
        {
          "@type": "PropertyValue",
          "name": "主要機能",
          "value": "スマホ遠隔操作・自動給排水・省力化・品質向上"
        }
      ]
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ホーム",
          "item": "https://ids-group.jp/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "水管理システム",
          "item": "https://ids-group.jp/water-management"
        }
      ]
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Farmo水管理システムの月額料金はいくらですか?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Farmo水管理システムは月額料金不要です。製品代のみでずっとご利用いただけます。追加費用の心配がなく、長期的にコスト削減が可能です。"
          }
        },
        {
          "@type": "Question",
          "name": "どのくらいの水田面積に対応していますか?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "小規模な水田から大規模圃場まで幅広く対応可能です。複数の水田を一括管理することもできます。お客様の圃場状況に応じた最適なシステム構成をご提案しますので、まずはお気軽にご相談ください。"
          }
        },
        {
          "@type": "Question",
          "name": "スマホの操作が苦手でも使えますか?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Farmo水管理システムは直感的な操作画面で、スマホ初心者の方でも簡単に使えます。導入時には丁寧な操作説明を行い、導入後もサポート体制が整っていますのでご安心ください。"
          }
        },
        {
          "@type": "Question",
          "name": "設置工事は自分でできますか?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "基本的な設置作業は比較的シンプルですが、確実な動作のためには専門スタッフによる設置をおすすめします。IDS教習所グループでは設置サポートも提供していますので、お気軽にご相談ください。"
          }
        },
        {
          "@type": "Question",
          "name": "購入後のサポート体制は?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "佐賀(IDS佐賀教習所・IDS川副教習所)・鹿児島(IDS鹿児島教習所)の3拠点すべてでFarmo水管理システムのサポートを提供しています。操作方法のご質問、トラブル対応、メンテナンスまで安心してご利用いただけます。"
          }
        },
        {
          "@type": "Question",
          "name": "補助金は使えますか?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "自治体や年度によって、スマート農業機器(水管理システム含む)の導入に関する補助金・助成金制度が用意されている場合があります。申請条件や補助率は地域や制度により異なりますので、まずはお気軽にご相談ください。最新の補助金情報もご案内いたします。"
          }
        }
      ]
    };

    // 既存のスクリプトタグを削除
    const existingProductScript = document.querySelector('script[data-schema="water-management-product"]');
    if (existingProductScript) {
      existingProductScript.remove();
    }

    const existingBreadcrumbScript = document.querySelector('script[data-schema="water-management-breadcrumb"]');
    if (existingBreadcrumbScript) {
      existingBreadcrumbScript.remove();
    }

    const existingFaqScript = document.querySelector('script[data-schema="water-management-faq"]');
    if (existingFaqScript) {
      existingFaqScript.remove();
    }

    // 新しいスクリプトタグを追加
    const productScriptTag = document.createElement('script');
    productScriptTag.type = 'application/ld+json';
    productScriptTag.setAttribute('data-schema', 'water-management-product');
    productScriptTag.textContent = JSON.stringify(productSchema);
    document.head.appendChild(productScriptTag);

    const breadcrumbScriptTag = document.createElement('script');
    breadcrumbScriptTag.type = 'application/ld+json';
    breadcrumbScriptTag.setAttribute('data-schema', 'water-management-breadcrumb');
    breadcrumbScriptTag.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScriptTag);

    const faqScriptTag = document.createElement('script');
    faqScriptTag.type = 'application/ld+json';
    faqScriptTag.setAttribute('data-schema', 'water-management-faq');
    faqScriptTag.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScriptTag);

    // クリーンアップ
    return () => {
      const productScript = document.querySelector('script[data-schema="water-management-product"]');
      if (productScript) {
        productScript.remove();
      }
      const breadcrumbScript = document.querySelector('script[data-schema="water-management-breadcrumb"]');
      if (breadcrumbScript) {
        breadcrumbScript.remove();
      }
      const faqScript = document.querySelector('script[data-schema="water-management-faq"]');
      if (faqScript) {
        faqScript.remove();
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // フォームデータにページ情報を追加
    const submitData = {
      formName: 'consultation-form',
      sourceUrl: window.location.href,
      submittedAt: new Date().toISOString(),
    };
    
    // フォームデータを追加
    formData.forEach((value, key) => {
      // @ts-ignore
      submitData[key] = value.toString();
    });

    try {
      const response = await fetch('https://n8n.srv1003858.hstgr.cloud/webhook/d6682ee0-8cb2-4bec-905a-ddf9d59b9685', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const paddyProducts = [
    {
      id: 1,
      name: '水位センサー',
      description: '遠くの田んぼの水位がわかる',
      features: [
        'スマホで水位確認',
        'マップ表示対応',
        '水温測定機能付きモデルあり',
        '月額料金不要'
      ],
      image: 'https://static.readdy.ai/image/b1b0be4a533f8dd52f529875d332f87d/2b434a02f3092db0449ccb0de1ce40b1.webp'
    },
    {
      id: 2,
      name: '給水ゲート',
      description: '簡単設置、スマホで給水',
      features: [
        'ワンタッチ設置',
        'スマホで遠隔操作',
        '自動給水・止水',
        '取り外し簡単'
      ],
      image: 'https://static.readdy.ai/image/b1b0be4a533f8dd52f529875d332f87d/4370e7d22fafb420f7d05d7d9e0425f7.webp'
    },
    {
      id: 3,
      name: '給水バルブ',
      description: 'バルブの操作をスマホで開閉',
      features: [
        '既存バルブに後付可能',
        'スマホで遠隔操作',
        '工事不要で簡単設置',
        'タイマー機能搭載'
      ],
      image: 'https://static.readdy.ai/image/b1b0be4a533f8dd52f529875d332f87d/d72567596e2c213e7e769b7353cf0360.webp'
    }
  ];

  const houseProducts = [
    {
      id: 1,
      name: 'ハウスファーモAタイプ',
      description: '育苗ハウスの温度管理',
      features: [
        'スマホで温度確認',
        '異常時通知機能',
        '設定温度アラート',
        '簡単設置'
      ],
      image: 'https://public.readdy.ai/ai/img_res/ce7c441c3123f6eb98ebf38de9d12294.jpg'
    },
    {
      id: 2,
      name: 'ハウスファーモBタイプ',
      description: '育苗など環境の総合管理',
      features: [
        '温度・湿度・照度測定',
        'スマホで環境確認',
        '複数センサー対応',
        'データログ機能'
      ],
      image: 'https://readdy.ai/api/search-image?query=Advanced%20greenhouse%20environmental%20monitoring%20system%20with%20digital%20display%20showing%20B%20Type%20text%20label%2C%20multiple%20sensors%20for%20temperature%20humidity%20and%20light%2C%20modern%20agricultural%20IoT%20device%2C%20clean%20white%20background%2C%20professional%20product%20photography&width=400&height=400&seq=house-b&orientation=squarish'
    },
    {
      id: 3,
      name: 'ハウスファーモCタイプ',
      description: 'にら、メロン栽培の環境管理',
      features: [
        '温度・湿度・照度・飽差測定',
        '地中温度監視',
        '地中湿度測定',
        '作物別最適化設定'
      ],
      image: 'https://readdy.ai/api/search-image?query=Comprehensive%20greenhouse%20monitoring%20device%20with%20digital%20display%20showing%20C%20Type%20text%20label%2C%20advanced%20sensors%20for%20soil%20and%20air%20monitoring%2C%20designed%20for%20vegetable%20cultivation%2C%20clean%20white%20background%2C%20professional%20product%20photography&width=400&height=400&seq=house-c&orientation=squarish'
    },
    {
      id: 4,
      name: 'ハウスファーモDタイプ',
      description: 'きのこ栽培の専用環境管理',
      features: [
        '温度・湿度・照度・飽差測定',
        '地中温度・生長点温度監視',
        'CO2濃度測定',
        'きのこ栽培最適化'
      ],
      image: 'https://readdy.ai/api/search-image?query=Specialized%20greenhouse%20monitoring%20system%20with%20digital%20display%20showing%20D%20Type%20text%20label%2C%20designed%20for%20mushroom%20cultivation%20with%20CO2%20sensors%2C%20advanced%20environmental%20control%20device%2C%20clean%20white%20background%2C%20professional%20product%20photography&width=400&height=400&seq=house-d&orientation=squarish'
    },
    {
      id: 5,
      name: 'ハウスファーモEタイプ',
      description: 'いちご栽培の高精度環境管理',
      features: [
        '温度・湿度・照度・飽差測定',
        '地中温度・生長点温度監視',
        'CO2濃度・地中湿度測定',
        'いちご栽培専用設計'
      ],
      image: 'https://readdy.ai/api/search-image?query=High-precision%20greenhouse%20monitoring%20device%20with%20digital%20display%20showing%20E%20Type%20text%20label%2C%20specialized%20for%20strawberry%20cultivation%20with%20multiple%20environmental%20sensors%2C%20modern%20agricultural%20technology%2C%20clean%20white%20background%2C%20professional%20product%20photography&width=400&height=400&seq=house-e&orientation=squarish'
    },
    {
      id: 6,
      name: 'ハウスファーモFタイプ',
      description: 'きゅうり栽培の環境管理',
      features: [
        '温度・湿度・照度・飽差測定',
        '地中温度・生長点温度監視',
        'CO2濃度・地中湿度測定',
        'きゅうり栽培最適化'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20greenhouse%20environmental%20control%20system%20with%20digital%20display%20showing%20F%20Type%20text%20label%2C%20optimized%20for%20cucumber%20cultivation%20with%20comprehensive%20sensor%20array%2C%20modern%20agricultural%20IoT%20device%2C%20clean%20white%20background&width=400&height=400&seq=house-f&orientation=squarish'
    },
    {
      id: 7,
      name: 'ハウスファーモGEタイプ',
      description: 'いちご栽培の最高級環境管理',
      features: [
        '全環境要素測定対応',
        '土壌水分・EC測定',
        '生長点温度・CO2監視',
        'プレミアム機能搭載'
      ],
      image: 'https://readdy.ai/api/search-image?query=Premium%20greenhouse%20monitoring%20system%20with%20digital%20display%20showing%20GE%20Type%20text%20label%2C%20top-tier%20environmental%20control%20for%20strawberry%20cultivation%20with%20soil%20moisture%20and%20EC%20sensors%2C%20advanced%20agricultural%20technology%2C%20clean%20white%20background&width=400&height=400&seq=house-ge&orientation=squarish'
    },
    {
      id: 8,
      name: 'ハウスファーモGFタイプ',
      description: 'トマト栽培の最高級環境管理',
      features: [
        '全環境要素測定対応',
        '土壌水分・EC測定',
        '生長点温度・CO2監視',
        'トマト栽培専用設計'
      ],
      image: 'https://readdy.ai/api/search-image?query=Top-tier%20greenhouse%20environmental%20monitoring%20device%20with%20digital%20display%20showing%20GF%20Type%20text%20label%2C%20specialized%20for%20tomato%20cultivation%20with%20complete%20sensor%20suite%20including%20soil%20moisture%20and%20EC%20measurement%2C%20clean%20white%20background&width=400&height=400&seq=house-gf&orientation=squarish'
    }
  ];

  const benefits = [
    {
      icon: 'ri-smartphone-line',
      title: 'スマホで簡単管理',
      description: 'いつでもどこでも、スマートフォンから水管理ができます'
    },
    {
      icon: 'ri-time-line',
      title: '省力化を実現',
      description: '水回りの時間を大幅に削減し、他の作業に集中できます'
    },
    {
      icon: 'ri-leaf-line',
      title: '品質向上',
      description: '適切な水管理により、お米の品質向上や収量アップが期待できます'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: '月額料金不要',
      description: '製品代のみでずっと利用でき、追加費用の心配がありません'
    }
  ];

  const currentProducts = activeTab === 'paddy' ? paddyProducts : houseProducts;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-50 to-green-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              水管理システム
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Farmoの水管理システムで、スマートな農業を実現。<br />
              いつでもどこでも、スマホで簡単に水管理ができます。
            </p>
            <div className="flex justify-center">
              <img 
                src="https://static.readdy.ai/image/b1b0be4a533f8dd52f529875d332f87d/c74d37b0faf2555e27403c10c6573906.webp" 
                alt="水管理システム" 
                className="max-w-4xl w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Farmo水管理システムの特徴
            </h2>
            <p className="text-lg text-gray-600">
              累計3万台を突破した、かんたん・安心の水管理定番ツール
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg">
                <div className="w-16 h-16 bg-[#76B947] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              製品ラインナップ
            </h2>
            <p className="text-lg text-gray-600">
              用途に応じて選べる管理システム
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-full p-1 shadow-md">
              <button
                onClick={() => setActiveTab('paddy')}
                className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'paddy'
                    ? 'bg-[#76B947] text-white'
                    : 'text-gray-600 hover:text-[#76B947]'
                }`}
              >
                水田用
              </button>
              <button
                onClick={() => setActiveTab('house')}
                className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'house'
                    ? 'bg-[#76B947] text-white'
                    : 'text-gray-600 hover:text-[#76B947]'
                }`}
              >
                ハウス用（環境モニタリング）
              </button>
            </div>
          </div>

          {/* Product Grid */}
          {activeTab === 'paddy' && (
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl">
                {currentProducts.map((product) => (
                  <div key={product.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                    <div className="aspect-square overflow-hidden">
                      <img 
                        alt={product.name} 
                        className="w-full h-full object-cover" 
                        src={product.image}
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                      <p className="text-gray-600 mb-4">{product.description}</p>
                      <ul className="space-y-2">
                        {product.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600">
                            <i className="ri-check-line text-[#76B947] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ハウス用（環境モニタリング）タブコンテンツ */}
          {activeTab === 'house' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {/* Aタイプ */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                  <div className="text-4xl font-bold text-gray-600">Aタイプ</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">ハウスファーモAタイプ</h3>
                  <p className="text-gray-600 mb-4">育苗ハウス専用</p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>気温測定
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>スマホで確認
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>プッシュ通知
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>簡単設置
                    </li>
                  </ul>
                </div>
              </div>

              {/* Bタイプ */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                  <div className="text-4xl font-bold text-gray-600">Bタイプ</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">ハウスファーモBタイプ</h3>
                  <p className="text-gray-600 mb-4">育苗など環境の総合管理</p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>気温・湿度・照度測定
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>環境データ一覧表示
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>グラフ表示機能
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>データ共有可能
                    </li>
                  </ul>
                </div>
              </div>

              {/* Cタイプ */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                  <div className="text-4xl font-bold text-gray-600">Cタイプ</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">ハウスファーモCタイプ</h3>
                  <p className="text-gray-600 mb-4">にら、メロン栽培対応</p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>気温・湿度・照度・飽差
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>地中温度・地中湿度
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>作物別最適化設定
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>5分間隔データ取得
                    </li>
                  </ul>
                </div>
              </div>

              {/* Dタイプ */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                  <div className="text-4xl font-bold text-gray-600">Dタイプ</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">ハウスファーモDタイプ</h3>
                  <p className="text-gray-600 mb-4">きのこ栽培専用設計</p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>気温・湿度・照度・飽差
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>地中温度・生長点温度
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>CO2濃度測定
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>きのこ栽培最適化
                    </li>
                  </ul>
                </div>
              </div>

              {/* Eタイプ */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                  <div className="text-4xl font-bold text-gray-600">Eタイプ</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">ハウスファーモEタイプ</h3>
                  <p className="text-gray-600 mb-4">いちご栽培高精度管理</p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>気温・湿度・照度・飽差
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>地中温度・生長点温度
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>CO2濃度・地中湿度
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>いちご栽培専用設計
                    </li>
                  </ul>
                </div>
              </div>

              {/* Fタイプ */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                  <div className="text-4xl font-bold text-gray-600">Fタイプ</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">ハウスファーモFタイプ</h3>
                  <p className="text-gray-600 mb-4">きゅうり栽培対応</p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>気温・湿度・照度・飽差
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>地中温度・生長点温度
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>CO2濃度・地中湿度
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>立体栽培最適化
                    </li>
                  </ul>
                </div>
              </div>

              {/* GEタイプ */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                  <div className="text-4xl font-bold text-gray-600">GEタイプ</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">ハウスファーモGEタイプ</h3>
                  <p className="text-gray-600 mb-4">いちご栽培最高級モデル</p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>全環境要素測定対応
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>土壌水分・EC測定
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>生長点温度・CO2監視
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>プレミアム機能搭載
                    </li>
                  </ul>
                </div>
              </div>

              {/* GFタイプ */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                  <div className="text-4xl font-bold text-gray-600">GFタイプ</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">ハウスファーモGFタイプ</h3>
                  <p className="text-gray-600 mb-4">トマト栽培最高級モデル</p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>全環境要素測定対応
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>土壌水分・EC測定
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>生長点温度・CO2監視
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#76B947] mr-2"></i>トマト栽培専用設計
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* お問い合わせボタン */}
          <div className="text-center mt-12">
            <button className="px-8 py-4 bg-[#76B947] text-white rounded-lg hover:bg-[#2A6041] transition-colors font-semibold text-lg whitespace-nowrap cursor-pointer">
              お問い合わせ
            </button>
          </div>

        </div>
      </section>

      {/* Manufacturer Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              取扱いメーカー
            </h2>
          </div>
          <div className="bg-gray-50 rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <img
                src="https://static.readdy.ai/image/b1b0be4a533f8dd52f529875d332f87d/49b7dc02bce026f5a7e0551b8a85a05b.webp"
                alt="Farmo"
                className="h-16 mx-auto"
              />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">株式会社Farmo</h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              ファーモはIoT製品とクラウドサービスの開発により、農家を支援し地域に貢献する、スマート農業推進企業です。
              累計3万台を突破した実績と信頼で、農業の未来を支えています。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-[#76B947] mb-2">3万台+</div>
                <div className="text-gray-600">累計出荷台数</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#76B947] mb-2">月額0円</div>
                <div className="text-gray-600">維持費用</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#76B947] mb-2">全国対応</div>
                <div className="text-gray-600">通信エリア</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50" data-cta-section>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2A6041] mb-4">
              水管理システムで農業をもっとスマートに
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              「うちの水田でも使える？」「設置は簡単？」「費用対効果は？」<br />
              どんな些細なことでも構いません。専門スタッフが、あなたの状況を丁寧にヒアリングし、<br />
              最適なプランをご提案します。相談は無料です。
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* お見積り・無料相談フォーム */}
            <div className="h-full">
              <form 
                id="consultation-form" 
                data-readdy-form="true" 
                className="bg-white rounded-2xl p-8 shadow-lg h-full flex flex-col"
                onSubmit={handleSubmit}
              >
                <h3 className="text-2xl font-bold text-[#2A6041] mb-6">お見積り・無料相談フォーム</h3>
                
                <div className="flex-1">
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        お名前 <span className="text-red-500">*</span>
                      </label>
                      <input 
                        required 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm" 
                        placeholder="山田太郎" 
                        name="name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">会社名・農場名</label>
                      <input 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm" 
                        placeholder="○○農場" 
                        name="company"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        電話番号 <span className="text-red-500">*</span>
                      </label>
                      <input 
                        required 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm" 
                        placeholder="090-1234-5678" 
                        type="tel" 
                        name="phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        メールアドレス <span className="text-red-500">*</span>
                      </label>
                      <input 
                        required 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm" 
                        placeholder="example@email.com" 
                        type="email" 
                        name="email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        都道府県 <span className="text-red-500">*</span>
                      </label>
                      <select name="prefecture" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm pr-8">
                        <option value="">選択してください</option>
                        <option value="佐賀県">佐賀県</option>
                        <option value="鹿児島県">鹿児島県</option>
                        <option value="福岡県">福岡県</option>
                        <option value="長崎県">長崎県</option>
                        <option value="熊本県">熊本県</option>
                        <option value="大分県">大分県</option>
                        <option value="宮崎県">宮崎県</option>
                        <option value="その他">その他</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">作物の種類</label>
                      <select name="cropType" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm pr-8">
                        <option value="">選択してください</option>
                        <option value="水稲">水稲</option>
                        <option value="麦">麦</option>
                        <option value="大豆">大豆</option>
                        <option value="野菜">野菜</option>
                        <option value="果樹">果樹</option>
                        <option value="その他">その他</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      農地面積（概算）
                    </label>
                    <select name="farmSize" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm pr-8">
                      <option value="">選択してください</option>
                      <option value="1ha未満">1ha未満</option>
                      <option value="1-5ha">1-5ha</option>
                      <option value="5-10ha">5-10ha</option>
                      <option value="10-20ha">10-20ha</option>
                      <option value="20ha以上">20ha以上</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      興味のある製品・サービス
                    </label>
                    <select name="interestedProducts" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm pr-8">
                      <option value="">選択してください</option>
                      <option value="農業ドローン">農業ドローン</option>
                      <option value="自動操舵システム">自動操舵システム</option>
                      <option value="水管理システム">水管理システム</option>
                      <option value="ドローンスクール">ドローンスクール</option>
                      <option value="まだ決まっていない">まだ決まっていない</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      問い合わせ先 <span className="text-red-500">*</span>
                    </label>
                    <select 
                      name="contactDestination" 
                      required 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm pr-8"
                    >
                      <option value="">選択してください</option>
                      <option value="IDS佐賀教習所（株式会社アイテム）">IDS佐賀教習所（株式会社アイテム）</option>
                      <option value="IDS川副教習所（九三エンジニアリング株式会社）">IDS川副教習所（九三エンジニアリング株式会社）</option>
                      <option value="IDS鹿児島教習所（株式会社エコナーセリー）">IDS鹿児島教習所（株式会社エコナーセリー）</option>
                      <option value="問合せ先がわからない">問合せ先がわからない</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#76B947] focus:border-transparent resize-none text-sm"
                      placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                    ></textarea>
                  </div>
                </div>

                <div className="mt-auto">
                  <button 
                    className="w-full py-4 bg-[#FFC107] text-black rounded-lg hover:bg-yellow-500 transition-colors font-bold text-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" 
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '送信中...' : '無料相談を申し込む'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-sm">お問い合わせありがとうございます。担当者より折り返しご連絡いたします。</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-sm">送信に失敗しました。お手数ですが、もう一度お試しください。</p>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* 電話相談セクション */}
            <div className="h-full">
              <div className="bg-white rounded-2xl p-8 shadow-lg h-full flex flex-col">
                <h3 className="text-2xl font-bold text-[#2A6041] mb-6 text-center">お電話で今すぐ相談したい方</h3>
                
                <div className="flex-1 flex flex-col justify-center">
                  <div className="space-y-8">
                    <div className="text-center border-b border-gray-100 pb-6">
                      <h4 className="font-bold text-[#76B947] mb-3 text-lg">IDS佐賀教習所</h4>
                      <p className="text-sm text-gray-600 mb-2">株式会社アイテム</p>
                      <div className="flex items-center justify-center mb-2">
                        <i className="ri-map-pin-line text-[#76B947] mr-2 text-sm"></i>
                        <p className="text-sm text-gray-600">佐賀県多久市北多久町大字小侍4771</p>
                      </div>
                      <a href="tel:0952-71-9855" className="inline-flex items-center text-2xl font-bold text-[#2A6041] hover:text-[#76B947] transition-colors cursor-pointer mb-1">
                        <i className="ri-phone-line mr-2"></i>
                        0952-71-9855
                      </a>
                      <p className="text-sm text-gray-500">受付時間: 平日9:00〜17:00</p>
                    </div>
                    
                    <div className="text-center border-b border-gray-100 pb-6">
                      <h4 className="font-bold text-[#76B947] mb-3 text-lg">IDS川副教習所</h4>
                      <p className="text-sm text-gray-600 mb-2">九三エンジニアリング株式会社</p>
                      <div className="flex items-center justify-center mb-2">
                        <i className="ri-map-pin-line text-[#76B947] mr-2 text-sm"></i>
                        <p className="text-sm text-gray-600">佐賀県佐賀市川副町小々森146-1</p>
                      </div>
                      <a href="tel:0952-97-5291" className="inline-flex items-center text-2xl font-bold text-[#2A6041] hover:text-[#76B947] transition-colors cursor-pointer mb-1">
                        <i className="ri-phone-line mr-2"></i>
                        0952-97-5291
                      </a>
                      <p className="text-sm text-gray-500">受付時間: 平日9:00〜17:00</p>
                    </div>
                    
                    <div className="text-center pb-4">
                      <h4 className="font-bold text-[#76B947] mb-3 text-lg">IDS鹿児島教習所</h4>
                      <p className="text-sm text-gray-600 mb-2">株式会社エコナーセリー</p>
                      <div className="flex items-center justify-center mb-2">
                        <i className="ri-map-pin-line text-[#76B947] mr-2 text-sm"></i>
                        <p className="text-sm text-gray-600">鹿児島県日置市伊集院町古城795</p>
                      </div>
                      <a href="tel:099-213-9466" className="inline-flex items-center text-2xl font-bold text-[#2A6041] hover:text-[#76B947] transition-colors cursor-pointer mb-1">
                        <i className="ri-phone-line mr-2"></i>
                        099-213-9466
                      </a>
                      <p className="text-sm text-gray-500">受付時間: 平日9:00〜17:00</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-auto pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500 text-center leading-relaxed">
                    無理な営業は一切いたしませんので、<br />
                    お気軽にご連絡ください。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WaterManagementPage;
